import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/berlin/Footer";
import { BrandWordmark } from "@/components/berlin/BrandWordmark";
import { useLang } from "@/lib/i18n";

/*
  Impressum · gemäß § 5 DDG + § 18 Abs. 2 MStV
  Anbieter: Martin Fichtelmann (privat) · Berlin-Cube · Berliner Allee 37–39 · 14641 Wustermark
*/

const T = {
  de: {
    back: "Zurück",
    kicker: "Rechtliches",
    h1: "Impressum",
    provider: "Angaben gem. § 5 DDG",
    contact: "Kontakt",
    phone: "Telefon",
    email: "E-Mail",
    responsible: "Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV",
    liability: "Haftungshinweise",
    liabilityText:
      "Trotz sorgfältiger inhaltlicher Kontrolle übernehmen wir keine Haftung für die Inhalte externer Links. Für den Inhalt der verlinkten Seiten sind ausschließlich deren Betreiber verantwortlich. Alle Inhalte dieser Website (Texte, Bilder, Grafiken, Renderings) sind urheberrechtlich geschützt. Jede Verwertung außerhalb der engen Grenzen des Urheberrechts bedarf der schriftlichen Zustimmung von Martin Fichtelmann.",
    dispute: "Streitschlichtung",
    disputeText:
      "Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.",
    home: "← Startseite",
    privacy: "Datenschutz →",
  },
  en: {
    back: "Back",
    kicker: "Legal",
    h1: "Legal notice",
    provider: "Information pursuant to § 5 DDG",
    contact: "Contact",
    phone: "Phone",
    email: "Email",
    responsible: "Responsible for content pursuant to § 18 (2) MStV",
    liability: "Liability notice",
    liabilityText:
      "Despite careful control of content, we accept no liability for the content of external links. The operators of linked pages are solely responsible for their content. All content on this website (texts, images, graphics, renderings) is protected by copyright. Any use beyond the narrow limits of copyright law requires the written consent of Martin Fichtelmann.",
    dispute: "Dispute resolution",
    disputeText:
      "We are neither willing nor obliged to participate in dispute resolution proceedings before a consumer arbitration board.",
    home: "← Home",
    privacy: "Privacy →",
  },
};

const Label = ({ children, gold = false }) => (
  <div className={`text-[10px] tracking-[0.35em] uppercase mb-3 font-display ${gold ? "text-[#F5D77A]" : "text-white/45"}`}>{children}</div>
);

export default function Impressum() {
  const { lang } = useLang();
  const t = T[lang] || T.de;
  const homeHref = lang === "en" ? "/en/" : "/";
  return (
    <div className="min-h-screen bg-[#1F2328] text-white" data-testid="page-impressum">
      <header className="border-b border-white/8">
        <div className="max-w-[1400px] mx-auto px-6 md:px-14 py-6 flex items-center justify-between">
          <Link
            to={homeHref}
            className="inline-flex items-center gap-2 font-display font-semibold tracking-[0.24em] text-sm text-white hover:text-[#F5D77A] transition-colors"
            data-testid="impressum-nav-home"
          >
            <BrandWordmark lineClass="bg-white/80" />
          </Link>
          <Link to={homeHref} className="inline-flex items-center gap-2 text-[11px] tracking-[0.32em] uppercase text-white/60 hover:text-white transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
            {t.back}
          </Link>
        </div>
      </header>

      <main className="max-w-[900px] mx-auto px-6 md:px-14 py-20 md:py-32">
        <div className="text-[11px] tracking-[0.35em] uppercase text-white/45 mb-8 flex items-center gap-3">
          <span className="h-px w-8 bg-[#F5D77A]" />
          {t.kicker}
        </div>
        <h1 className="font-display font-light tracking-[-0.03em] leading-[1] text-white mb-16" style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}>
          {t.h1}
        </h1>

        <div className="space-y-10 text-white/80 leading-relaxed max-w-2xl">
          <section data-testid="impressum-provider">
            <Label gold>{t.provider}</Label>
            <div className="font-display text-2xl md:text-3xl text-white font-light tracking-[-0.01em]" translate="no">
              Martin Fichtelmann
            </div>
            <p className="mt-4 text-white/70" translate="no">
              Berlin-Cube<br />
              Berliner Allee 37–39<br />
              14641 Wustermark
            </p>
          </section>

          <section>
            <Label>{t.contact}</Label>
            <div className="grid sm:grid-cols-2 gap-y-2 gap-x-8 text-white/80">
              <div>{t.phone}</div>
              <a href="tel:+4917613600041" className="text-white hover:text-[#F5D77A] transition-colors" data-testid="impressum-phone" translate="no">
                +49 176 13600041
              </a>
              <div>{t.email}</div>
              <a href="mailto:info@berlin-cube.de" className="text-white hover:text-[#F5D77A] transition-colors" data-testid="impressum-email" translate="no">
                info@berlin-cube.de
              </a>
            </div>
          </section>

          <section data-testid="impressum-responsible">
            <Label>{t.responsible}</Label>
            <p className="text-white/85" translate="no">Martin Fichtelmann, Berliner Allee 37–39, 14641 Wustermark</p>
          </section>

          <section className="pt-8 border-t border-white/10">
            <Label>{t.liability}</Label>
            <p className="text-white/70 text-sm leading-relaxed" data-testid="impressum-liability">{t.liabilityText}</p>
          </section>

          <section>
            <Label>{t.dispute}</Label>
            <p className="text-white/70 text-sm leading-relaxed" data-testid="impressum-dispute">{t.disputeText}</p>
          </section>
        </div>

        <div className="mt-20 pt-8 border-t border-white/10 flex items-center justify-between text-[11px] tracking-[0.28em] uppercase text-white/40">
          <Link to={homeHref} className="hover:text-[#F5D77A] transition-colors">{t.home}</Link>
          <Link to="/datenschutz" className="hover:text-[#F5D77A] transition-colors" data-testid="impressum-link-datenschutz">{t.privacy}</Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
